// ---------------------------------------------------------------------------
// viewsClient — typed fetchers for the backend's /api/v2/views/* view-model
// endpoints ("one call per view", composition on the backend).
//
// Each endpoint replaces a client-side composition path:
//   schedule              → usePlannerSchedule's appointments+bots+recents merge
//   session-detail        → PostSessionNotesView's 4-call mount
//   scribe-start          → ScribePage's pre-start encounter verify + guard
//   patients-unified      → buildUnifiedPatientList's local+EHR roster merge
//   admin-dashboard       → adminService.aggregateConversationMetrics
//   admin-recent-sessions → adminService.getAllConversationsByFacilities +
//                           buildRecentSessionRows (Sessions tab list)
//   ehr-cache-health      → CacheHealthTab's four Firestore subscriptions
//   patient-profile       → patient timeline / stats stitching
//
// Transport is the shared apiClient.request (Firebase bearer token,
// {success, data} envelope unwrapping, 45s stall guard). Types mirror the
// route responses in calliope-backend src/routes/v2-views.ts /
// v2-views-data.ts — keep in sync.
// ---------------------------------------------------------------------------

import {request} from './apiClient';
import {tagsToLegacyFields, type RecentSessionRow} from './dataLayerClient';

// ─── GET /api/v2/views/schedule ──────────────────────────────────────────────

export type ScheduleSectionKey =
  | 'upcoming'
  | 'incomplete'
  | 'completed'
  | 'noShows'
  | 'later';

export type ScheduleRowKind = 'appointment' | 'encounter' | 'bot';
export type SchedulePatientMatchKind =
  | 'linked'
  | 'localOnly'
  | 'ehrOnly'
  | 'unknown';
export type ScheduleNoteStatus = 'none' | 'generated' | 'pushed';

export interface ScheduleRowPatient {
  patientId: string | null;
  ehrPatientId: string | null;
  name: string;
  dob: string | null;
  language: string | null;
  mrn: string | null;
  /** Which record supplied the demographics. */
  source: 'ehr' | 'local' | 'inline';
}

/** An encounter contributor — providerUserId resolved to a display name. */
export interface ScheduleEncounterContributor {
  userId: string;
  name: string | null;
}

export interface ScheduleViewRow {
  kind: ScheduleRowKind;
  matchKind: SchedulePatientMatchKind;
  time: {startIso: string | null; endIso: string | null};
  patient: ScheduleRowPatient;
  appointment?: {
    id: string;
    providerId: string | null;
    providerName: string | null;
    status: string | null;
    reason: string | null;
    facility: string | null;
  };
  encounter?: {
    encounterId: string;
    sessionIds: string[];
    status: 'open' | 'closed';
    noteStatus: ScheduleNoteStatus;
    totalMinutes: number;
    summary: string;
    /** Union of member session tags (Session.tags vocabulary). */
    tags: string[];
    /** Unique member providerUserIds in session order. */
    providerUserIds: string[];
    /** providerUserIds resolved to {userId, name} (contributor line). */
    contributors: ScheduleEncounterContributor[];
  };
  bot?: {
    id: string;
    meetingUrl: string | null;
    status: string | null;
    /** Bot workflow ('scribe' or a workflow_type string) — modality badge. */
    mode: string | null;
  };
}

export type ScheduleProviderScopeMode =
  | 'auto'
  | 'manual'
  | 'all'
  | 'needs-selection'
  | 'none';

export interface ScheduleProviderOption {
  key: string;
  name: string;
  count: number;
}

export interface ScheduleViewResponse {
  date: string;
  timeZone: string;
  ehr: {enabled: boolean; type: string | null};
  providerScope: {
    mode: ScheduleProviderScopeMode;
    matchedProviderId: string | null;
    providers: ScheduleProviderOption[];
  };
  sections: Record<ScheduleSectionKey, ScheduleViewRow[]>;
}

export async function fetchScheduleView({
  date,
  tz,
  providerKeys,
  allProviders,
  signal,
}: {
  date?: string | null;
  tz?: string | null;
  /** appointmentProviderKey values ("id:123" / "name:jane doe"). */
  providerKeys?: string[] | null;
  allProviders?: boolean;
  signal?: AbortSignal;
} = {}): Promise<ScheduleViewResponse> {
  return request('/api/v2/views/schedule', {
    query: {
      date: date || undefined,
      tz: tz || undefined,
      providerKeys: providerKeys?.length ? providerKeys.join(',') : undefined,
      allProviders: allProviders ? 'true' : undefined,
    },
    signal,
  });
}

// ─── GET /api/v2/views/session-detail/:sessionId ─────────────────────────────

/** Legacy Message doc shape with ISO-string timestamps (server-serialized). */
export type ViewLegacyMessage = Record<string, any> & {
  id?: string;
  content_type?: string;
  timestamp?: string | null;
};

export interface SessionDetailEncounterSession {
  sessionId: string;
  providerUserId: string | null;
  status: string | null;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number;
  tags: string[];
}

export interface SessionDetailEncounter {
  encounterId: string;
  patientId: string | null;
  ehrPatientId: string | null;
  patientName: string | null;
  summary: string;
  visibility: string | null;
  status: 'open' | 'closed';
  providerUserIds: string[];
  sessionsNum: number;
  sessionsTotalMin: number;
  encounterNoteIds: string[];
  createdAt: string | null;
  updatedAt: string | null;
  sessions: SessionDetailEncounterSession[];
}

export interface SessionDetailPatientIdentity {
  patientId: string | null;
  ehrPatientId: string | null;
  patientName: string | null;
}

export interface SessionDetailSession extends Record<string, any> {
  sessionId: string;
  encounterId: string | null;
  providerUserId: string | null;
  status: string | null;
  tags: string[];
  language: string | null;
  dialect: string | null;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number;
  encounter: {
    patientId: string | null;
    patientName: string | null;
    summary: string;
    sessionsNum: number;
    visibility: string | null;
    encounterNoteIds: string[];
  } | null;
  /** Authoritative patient identity for the mismatch check. */
  patient: SessionDetailPatientIdentity;
}

export interface EhrConfigView {
  id: string;
  ehrType: string | null;
  status: string | null;
  timeZone: string | null;
}

export interface SessionDetailTemplatesBlock {
  custom: Record<string, any>[];
  ehr: Record<string, any>[];
  ehrConfig: EhrConfigView | null;
  ehrEnabled: boolean;
}

export interface SessionDetailExistingNote {
  noteId: string;
  encounterId: string | null;
  providerUserId: string | null;
  templateId: string | null;
  pushedToEhr: boolean;
  ehrPushedAt: string | null;
  sourceSessionIds: string[];
  createdAt: string | null;
  updatedAt: string | null;
  sections: Array<{
    sectionId: string;
    key: string;
    title: string;
    content: string;
    order: number;
    lastEditedAt: string | null;
    lastEditedBy: string | null;
  }>;
}

export interface SessionDetailDraft {
  conversationId: string;
  patientId: string | null;
  ehrPatientId: string | null;
  encounterId: string | null;
  templateId: string | null;
  templateName: string | null;
  sections: Record<string, unknown>;
  rawTranscription: string;
  generationMode: string | null;
  status: string | null;
  updatedAt: string | null;
  expiresAt: string | null;
}

export interface SessionDetailViewResponse {
  session: SessionDetailSession;
  encounter: SessionDetailEncounter | null;
  /** This session's transcript (legacy message shape, ISO timestamps). */
  messages: ViewLegacyMessage[];
  /** Encounter-wide transcript with segment markers (equals `messages` for
   *  single-session encounters). Replaces loadEncounterAwareMessages. */
  aggregatedMessages: ViewLegacyMessage[];
  templates?: SessionDetailTemplatesBlock | null;
  existingNote?: SessionDetailExistingNote | null;
  draft?: SessionDetailDraft | null;
}

export async function fetchSessionDetailView(
  sessionId: string,
  {
    include = 'templates,note',
    tz,
    signal,
  }: {include?: string; tz?: string | null; signal?: AbortSignal} = {},
): Promise<SessionDetailViewResponse> {
  return request(
    `/api/v2/views/session-detail/${encodeURIComponent(sessionId)}`,
    {query: {include: include || undefined, tz: tz || undefined}, signal},
  );
}

// ─── GET /api/v2/views/scribe-start ──────────────────────────────────────────

export interface ScribeStartPatient extends Record<string, any> {
  patientId: string;
  firstName: string;
  lastName: string;
  preferredLanguage: string | null;
  preferredDialect: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  mrn: string | null;
  phoneNumber: string | null;
  email: string | null;
  patientEHRLinkId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ScribeStartViewResponse {
  patient: ScribeStartPatient | null;
  /** The patient's recent encounters (continue candidates), detail-row shape. */
  encounters: SessionDetailEncounter[];
  /** Validated continue target — null unless the server verified it belongs
   *  to the requested patient and the caller may use it. */
  continueEncounter: SessionDetailEncounter | null;
  /** True when continueEncounterId provably belongs to a DIFFERENT patient —
   *  the client must start a standalone session (mirror of the web guard). */
  crossPatientBlocked: boolean;
  templates: SessionDetailTemplatesBlock;
}

export async function fetchScribeStartView({
  patientId,
  ehrPatientId,
  continueEncounterId,
  signal,
}: {
  patientId?: string | null;
  ehrPatientId?: string | null;
  continueEncounterId?: string | null;
  signal?: AbortSignal;
} = {}): Promise<ScribeStartViewResponse> {
  return request('/api/v2/views/scribe-start', {
    query: {
      patientId: patientId || undefined,
      ehrPatientId: ehrPatientId || undefined,
      continueEncounterId: continueEncounterId || undefined,
    },
    signal,
  });
}

// ─── GET /api/v2/views/patients-unified ──────────────────────────────────────

export type UnifiedAppointmentStatus =
  | 'now'
  | 'soon'
  | 'justEnded'
  | 'laterToday'
  | 'past'
  | 'future'
  | 'unknown';

export interface UnifiedAppointmentView {
  id: string;
  ehrPatientId: string | null;
  providerId: string | null;
  providerName: string | null;
  resourceProviderIds: string[];
  appointmentDate: string;
  appointmentTime: string;
  facility: string | null;
  reason: string | null;
  visitType: string | null;
  ehrStatus: string | null;
  status: UnifiedAppointmentStatus;
  startsAt: string | null;
  endsAt: string | null;
  minutesUntilStart: number | null;
  minutesSinceEnd: number | null;
}

export interface UnifiedLocalPatient extends Record<string, any> {
  patientId: string;
  firstName: string;
  lastName: string;
  preferredLanguage: string | null;
  preferredDialect: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  mrn: string | null;
  phoneNumber: string | null;
  email: string | null;
  patientEHRLinkId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface UnifiedEhrPatient extends Record<string, any> {
  ehrPatientId: string;
  cacheDocumentId: string;
  ehrType: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  displayName: string | null;
  fullName: string | null;
  dob: string | null;
  gender: string | null;
  email: string | null;
  phone: string | null;
  mrn: string | null;
  accountNo: string | null;
  language: string | null;
  pcp: string | null;
  pcpId: string | null;
}

export interface UnifiedPatientViewRow {
  id: string;
  matchKind: 'local' | 'ehr' | 'linked';
  mergedBy: 'ehrLink' | 'demographics' | null;
  displayName: string;
  sortName: string;
  local: UnifiedLocalPatient | null;
  ehr: UnifiedEhrPatient | null;
  appointments?: UnifiedAppointmentView[];
}

export interface UnifiedPatientsViewResponse {
  patients: UnifiedPatientViewRow[];
  nextCursor: string | null;
  facilityTimeZone: string;
  ehr: {enabled: boolean; type: string | null};
}

export async function fetchUnifiedPatientsView({
  limit,
  cursor,
  q,
  includeAppointments,
  signal,
}: {
  limit?: number;
  cursor?: string | null;
  q?: string | null;
  includeAppointments?: boolean;
  signal?: AbortSignal;
} = {}): Promise<UnifiedPatientsViewResponse> {
  return request('/api/v2/views/patients-unified', {
    query: {
      limit,
      cursor: cursor || undefined,
      q: q || undefined,
      includeAppointments: includeAppointments ? 'true' : undefined,
    },
    signal,
  });
}

// ─── GET /api/v2/views/admin-dashboard ───────────────────────────────────────

export interface DashboardOverview {
  totalSessions: number;
  totalMinutes: number;
  interpretationMinutes: number;
  scribeMinutes: number;
  activeProviders: number;
}

export interface DashboardProviderUsage {
  userId: string;
  name: string;
  sessions: number;
  minutes: number;
}

export interface DashboardLanguageUsage {
  language: string;
  sessions: number;
  minutes: number;
}

export interface DashboardDailyUsage {
  date: string;
  sessions: number;
  minutes: number;
}

export interface AdminDashboardViewResponse {
  companyId: string;
  startDate: string;
  endDate: string;
  timeZone: string;
  clamped: boolean;
  truncated: boolean;
  overview?: DashboardOverview;
  providerUsage?: DashboardProviderUsage[];
  languages?: DashboardLanguageUsage[];
  daily?: DashboardDailyUsage[];
}

export async function fetchAdminDashboardView({
  companyId,
  facilityIds,
  startDate,
  endDate,
  include,
  tz,
  signal,
}: {
  companyId?: string | null;
  facilityIds?: string[] | null;
  startDate?: string | null;
  endDate?: string | null;
  include?: string | null;
  tz?: string | null;
  signal?: AbortSignal;
} = {}): Promise<AdminDashboardViewResponse> {
  return request('/api/v2/views/admin-dashboard', {
    query: {
      companyId: companyId || undefined,
      facilityIds: facilityIds?.length ? facilityIds.join(',') : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      include: include || undefined,
      tz: tz || undefined,
    },
    signal,
  });
}

// ─── GET /api/v2/views/admin-usage-by-department ─────────────────────────────

export interface DashboardDepartmentUsage {
  /** Stable department key: an EHR facility id ("29") or an
   *  account-mapping slug ("acct:internal-medicine"). */
  departmentId: string;
  name: string;
  sessions: number;
  minutes: number;
  interpretationMinutes: number;
  scribeMinutes: number;
  /** Document-translation pages/jobs uploaded by this department's users. */
  documentPages: number;
  documentJobs: number;
  activeProviders: number;
}

export interface DepartmentUnattributedUsage {
  sessions: number;
  minutes: number;
  interpretationMinutes: number;
  scribeMinutes: number;
  documentPages: number;
  documentJobs: number;
  users: number;
}

export interface DepartmentUsageTotals {
  sessions: number;
  minutes: number;
  interpretationMinutes: number;
  scribeMinutes: number;
  documentPages: number;
  documentJobs: number;
  /** Share of minutes attributed to a department, 0–100. */
  attributedPct: number;
}

export interface AdminUsageByDepartmentViewResponse {
  companyId: string;
  startDate: string;
  endDate: string;
  clamped: boolean;
  truncated?: boolean;
  /** False when the company has no attribution scheme (no EHR provider
   *  cache and no department-account mapping) — hide the card entirely. */
  available: boolean;
  /** Which scheme(s) produced the rows: EHR provider matching, department
   *  account emails, or both. Null when unavailable. */
  mode: 'ehr' | 'accounts' | 'hybrid' | null;
  departments: DashboardDepartmentUsage[];
  unattributed: DepartmentUnattributedUsage | null;
  totals: DepartmentUsageTotals | null;
}

export async function fetchAdminUsageByDepartmentView({
  companyId,
  startDate,
  endDate,
  signal,
}: {
  companyId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  signal?: AbortSignal;
} = {}): Promise<AdminUsageByDepartmentViewResponse> {
  return request('/api/v2/views/admin-usage-by-department', {
    query: {
      companyId: companyId || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    },
    signal,
  });
}

// ─── GET /api/v2/views/admin-recent-sessions ─────────────────────────────────

/** One admin Sessions-tab row: the shared /sessions/recent row shape
 *  (recentSessionRow + decorated encounter block) + the session's facility. */
export interface AdminRecentSessionRow extends RecentSessionRow {
  facilityId: string | null;
}

export interface AdminRecentSessionsViewResponse {
  sessions: AdminRecentSessionRow[];
  /** startedAt ISO of the last FETCHED doc (may be non-null even when a
   *  facility-filtered page returns fewer than `limit` rows). */
  nextCursor: string | null;
}

export async function fetchAdminRecentSessionsView({
  companyId,
  facilityIds,
  limit,
  cursor,
  signal,
}: {
  companyId?: string | null;
  facilityIds?: string[] | null;
  limit?: number;
  cursor?: string | null;
  signal?: AbortSignal;
} = {}): Promise<AdminRecentSessionsViewResponse> {
  return request('/api/v2/views/admin-recent-sessions', {
    query: {
      companyId: companyId || undefined,
      facilityIds: facilityIds?.length ? facilityIds.join(',') : undefined,
      limit,
      cursor: cursor || undefined,
    },
    signal,
  });
}

const ADMIN_RECENT_SESSIONS_PAGE_LIMIT = 100;
/** Pages scanned per window fetch — bounds the cursor walk on huge companies
 *  (the legacy Firestore path scanned the whole range; 20 × 100-row pages
 *  covers the 180-day maximum for realistic volumes). */
const ADMIN_RECENT_SESSIONS_MAX_PAGES = 20;

/**
 * Every admin recent-session row whose startedAt falls inside
 * [startDate, endDate], newest first. Pages the cursor endpoint backwards
 * until the fetched window passes startDate (nextCursor is the startedAt of
 * the last fetched doc, so it doubles as the "how far back did the server
 * read" probe even when facility filtering empties a page).
 */
export async function fetchAdminRecentSessionsWindow({
  companyId,
  facilityIds,
  startDate,
  endDate,
  signal,
}: {
  companyId?: string | null;
  facilityIds?: string[] | null;
  startDate: Date;
  endDate?: Date | null;
  signal?: AbortSignal;
}): Promise<AdminRecentSessionRow[]> {
  const startMs = startDate.getTime();
  const endMs = endDate ? endDate.getTime() : Number.POSITIVE_INFINITY;
  const rows: AdminRecentSessionRow[] = [];
  let cursor: string | null = null;

  for (let page = 0; page < ADMIN_RECENT_SESSIONS_MAX_PAGES; page += 1) {
    const response = await fetchAdminRecentSessionsView({
      companyId,
      facilityIds,
      limit: ADMIN_RECENT_SESSIONS_PAGE_LIMIT,
      cursor,
      signal,
    });
    for (const row of response.sessions || []) {
      const startedMs = row.startedAt ? new Date(row.startedAt).getTime() : NaN;
      if (Number.isNaN(startedMs)) continue;
      if (startedMs >= startMs && startedMs <= endMs) rows.push(row);
    }
    cursor = response.nextCursor;
    if (!cursor) break;
    const cursorMs = new Date(cursor).getTime();
    if (!Number.isNaN(cursorMs) && cursorMs < startMs) break;
  }
  return rows;
}

// ─── GET /api/v2/views/ehr-cache-health/:companyId ───────────────────────────

export interface CacheHealthRetryRow {
  id: string;
  patientId: string | null;
  providerName: string | null;
  date: string | null;
  attempts: number;
  lastError: string | null;
  lastSyncMode: string | null;
  lastRunId: string | null;
  firstFailedAt: string | null;
  lastFailedAt: string | null;
}

export interface CacheHealthPermanentFailureRow {
  id: string;
  kind: string | null;
  targetId: string | null;
  patientId: string | null;
  providerName: string | null;
  date: string | null;
  attempts: number;
  lastError: string | null;
  lastSyncMode: string | null;
  lastRunId: string | null;
  firstFailedAt: string | null;
  lastFailedAt: string | null;
  recordedAt: string | null;
  dismissedAt: string | null;
  dismissedBy: string | null;
}

export interface CacheHealthQueueBlock {
  count: number;
  oldest: string | null;
  rows: CacheHealthRetryRow[];
}

export interface EhrCacheHealthViewResponse {
  companyId: string;
  fetchedAt: string;
  enrichmentRetry: CacheHealthQueueBlock;
  encounterRetry: CacheHealthQueueBlock;
  appointmentsRetry: CacheHealthQueueBlock;
  permanentFailures: CacheHealthPermanentFailureRow[];
}

export async function fetchEhrCacheHealthView(
  companyId: string,
  {signal}: {signal?: AbortSignal} = {},
): Promise<EhrCacheHealthViewResponse> {
  return request(
    `/api/v2/views/ehr-cache-health/${encodeURIComponent(companyId)}`,
    {signal},
  );
}

// ─── GET /api/v2/views/patient-profile/:patientId ────────────────────────────

export interface PatientProfileStats {
  totalSessions: number;
  interpreterSessions: number;
  scribeSessions: number;
  firstSessionAt: string | null;
  lastSessionAt: string | null;
  totalMinutes: number;
}

export interface PatientProfileEncounterRow {
  encounterId: string;
  summary: string;
  sessionsNum: number;
  sessionsTotalMin: number;
  providerUserIds: string[];
  encounterNoteIds: string[];
  createdAt: string | null;
  updatedAt: string | null;
  sessions: Array<{
    sessionId: string;
    status: string | null;
    startedAt: string | null;
    durationSeconds: number;
    tags: string[];
  }>;
}

export interface PatientProfileViewResponse {
  patientId: string;
  patient: UnifiedLocalPatient;
  encounters?: PatientProfileEncounterRow[];
  /** Recents-row shape + removedAt (the patient's soft-deleted sessions). */
  removedSessions?: Array<Record<string, any>>;
  stats?: PatientProfileStats;
}

export async function fetchPatientProfileView(
  patientId: string,
  {include, signal}: {include?: string | null; signal?: AbortSignal} = {},
): Promise<PatientProfileViewResponse> {
  return request(
    `/api/v2/views/patient-profile/${encodeURIComponent(patientId)}`,
    {query: {include: include || undefined}, signal},
  );
}

// ─── View → legacy view-model mappers ────────────────────────────────────────

const toJsDateOrEpoch = (iso: unknown): Date => {
  if (!iso) return new Date(0);
  const parsed = new Date(iso as string);
  return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
};

const toJsDateOrNull = (iso: unknown): Date | null => {
  if (!iso) return null;
  const parsed = new Date(iso as string);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Session-detail view messages (legacy Message shape with ISO-string
 * timestamps, serialized server-side) → the client's legacy message
 * view-model with Date timestamps, matching what
 * patientService.getSessionMessages produced.
 */
export function viewMessagesToLegacyMessages(
  messages: ViewLegacyMessage[] = [],
): Record<string, any>[] {
  return messages.map(message => {
    const structured = message.structured_output as
      | Record<string, any>
      | undefined;
    return {
      ...message,
      timestamp: toJsDateOrEpoch(message.timestamp),
      ...(structured && structured.generated_at !== undefined
        ? {
            structured_output: {
              ...structured,
              generated_at: toJsDateOrNull(structured.generated_at),
            },
          }
        : {}),
    };
  });
}

/**
 * Session-detail encounter row → the legacy Encounter-doc shape
 * encounterService.getEncounter returns (so PostSessionNotesView's
 * process-workflow encounter state keeps its field contract). Unlike
 * getEncounter's v1 source, the view row carries the external
 * `ehrPatientId`.
 */
export function sessionDetailEncounterToLegacyEncounterDoc(
  row: SessionDetailEncounter | null | undefined,
): Record<string, any> | null {
  if (!row?.encounterId) return null;
  const sessions = Array.isArray(row.sessions) ? row.sessions : [];
  const anyOpen =
    row.status === 'open' ||
    sessions.some(s => s.status === 'active' || s.status === 'created');
  const startTimes = sessions
    .map(s => toJsDateOrNull(s.startedAt))
    .filter(Boolean) as Date[];
  const legacy = tagsToLegacyFields(sessions[0]?.tags || []);
  return {
    id: row.encounterId,
    patient_id: row.patientId ?? null,
    ehr_patient_id: row.ehrPatientId ?? null,
    patient_name: row.patientName || '',
    company_id: null,
    facility_id: '',
    contributor_user_ids: row.providerUserIds || [],
    conversation_ids: sessions.map(s => s.sessionId).filter(Boolean),
    workflow_type: legacy.workflowType || 'medical_scribing',
    template_type: legacy.templateType || 'soap',
    specialty: legacy.specialty || '',
    status: anyOpen ? 'open' : 'closed',
    start_time: startTimes.length
      ? new Date(Math.min(...startTimes.map(d => d.getTime())))
      : toJsDateOrNull(row.createdAt),
    end_time: anyOpen ? null : toJsDateOrNull(row.updatedAt),
    total_minutes: Number(row.sessionsTotalMin) || 0,
    summary: row.summary || '',
    visibility: row.visibility || null,
    is_removed: false,
    created_at: toJsDateOrNull(row.createdAt),
    updated_at: toJsDateOrNull(row.updatedAt),
    encounterNoteIds: row.encounterNoteIds || [],
    _v2: true,
    _v2Sessions: sessions,
  };
}

/**
 * Session-detail draft block (camelCase, ISO timestamps) → the raw
 * ScribeDraft doc shape scribeService.mapScribeDraftToRecoveredNote consumes
 * (snake_case; its timestamp coercion parses ISO strings).
 */
export function sessionDetailDraftToScribeDraft(
  draft: SessionDetailDraft | null | undefined,
): Record<string, any> | null {
  if (!draft) return null;
  return {
    conversation_id: draft.conversationId,
    patient_id: draft.patientId,
    ehr_patient_id: draft.ehrPatientId,
    encounter_id: draft.encounterId,
    template_id: draft.templateId,
    template_name: draft.templateName,
    sections: draft.sections || {},
    raw_transcription: draft.rawTranscription || '',
    generation_mode: draft.generationMode,
    status: draft.status,
    updated_at: draft.updatedAt,
    expires_at: draft.expiresAt,
  };
}

const viewsClient = {
  fetchScheduleView,
  fetchSessionDetailView,
  fetchScribeStartView,
  fetchUnifiedPatientsView,
  fetchAdminDashboardView,
  fetchAdminRecentSessionsView,
  fetchAdminRecentSessionsWindow,
  fetchEhrCacheHealthView,
  fetchPatientProfileView,
  viewMessagesToLegacyMessages,
  sessionDetailEncounterToLegacyEncounterDoc,
  sessionDetailDraftToScribeDraft,
};

export default viewsClient;
